import { useQuery } from 'react-query';
import { TypeInsurance, TypeInsuranceFields } from 'types/contentful/TypeInsurance';
import ContentService from 'utils/contentful/contentfulService';
import * as Sentry from '@sentry/react';

export interface RescheduleInsuranceData {
  primary: TypeInsurance | undefined;
  alternates: TypeInsurance[];
}

interface UseRescheduleInsuranceParams {
  insurancePayer?: string;
  insuredState?: string;
  insuranceNameContentful?: string;
  enabled?: boolean;
}

export const useRescheduleInsurance = ({
  insurancePayer,
  insuredState,
  insuranceNameContentful,
  enabled = true,
}: UseRescheduleInsuranceParams) => {
  if (!insuranceNameContentful || !insuredState || !insurancePayer) {
    Sentry.captureException(new Error('ERROR: Expected insurance name/payer from reschedule info to be set'), {
      extra: {
        insurancePayer,
        insuranceNameContentful,
        insuredState,
      },
    });
  }

  return useQuery(
    ['reschedule-contentful-insurance', insurancePayer, insuredState],
    async () => {
      if (!insurancePayer || !insuredState) return undefined;

      // Get ALL insurance entries that match payerId and state
      const data = await ContentService.clients.expressable.getEntries<TypeInsuranceFields>({
        content_type: 'insurance',
        'fields.payerId[match]': insurancePayer,
        'fields.state[match]': insuredState,
      });

      if (!data.items || data.items.length === 0) {
        return undefined;
      }

      // Sort to prioritize the primary insurance name first
      const sorted = data.items.sort((a, b) => {
        const aIsMatch = a.fields.insuranceName === insuranceNameContentful ? 0 : 1;
        const bIsMatch = b.fields.insuranceName === insuranceNameContentful ? 0 : 1;
        return aIsMatch - bIsMatch;
      });

      const primary = sorted[0] as TypeInsurance;
      const alternates = sorted.slice(1) as TypeInsurance[];

      return {
        primary,
        alternates,
      } as RescheduleInsuranceData;
    },
    {
      enabled: enabled && !!insurancePayer && !!insuredState,
    },
  );
};

